import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import type APIAddress from '~/types/APIAddress';
import type Address from '~/types/Address';
import type Order from '~/types/Order';
import type Parcel from '~/types/Parcel';
import type ParcelTemplate from '~/types/ParcelTemplate';
import type Rate from '~/types/Rate';

import { type CountryIso2 } from '~/constants/countries';
import { singleOrMultiSelectionShouldShowCustoms } from '~/pages/Orders/Blackbeard/Drawers/LabelPurchaseDrawer/utils';
import { useInsurance } from '~/pages/Settings/Insurance/hook';
import { getRatesForBatchEntriesToBuy } from '~/selectors/batch-entry.composite.selectors';
import { getBatchEntryData } from '~/selectors/batch-entry.selectors';
import { getPaymentMethods } from '~/selectors/billing.selectors';
import { getLabelSettings } from '~/selectors/label-settings.selectors';
import { getOrdersData, getSelectedOrders } from '~/selectors/orders.selectors';
import { getDefaultParcel, getUserAddress, getUserReturnAddress } from '~/selectors/user.selectors';
import { type ApplicationState } from '~/store';
import { type InsuranceSettings } from '~/types/Insurance';
import { type OrderSelection } from '~/types/Order';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { PaymentMethodType } from '~/types/PaymentMethods';
import { isInsuranceAvailableForCountries } from '~/utils/insurance';

import { trackingHelper } from './index';
import { type TrackedOrderDetails, type TrackingReference } from './types';

export const useTrackedOrderDetailsAndUserData = (selectedOrders: OrderSelection) => {
  const batchEntryIds = getBatchEntryIds(selectedOrders);
  const ordersData = useSelector(getOrdersData);
  const batchEntries = useSelector(getBatchEntryData);
  const ratesForOrders = useSelector((state: ApplicationState) =>
    getRatesForBatchEntriesToBuy(state, { batchEntryIds }),
  );
  const defaultSenderAddress = useSelector(getUserAddress);
  const defaultReturnAddress = useSelector(getUserReturnAddress);
  const defaultParcelTemplate = useSelector(getDefaultParcel);
  const paymentMethods = useSelector(getPaymentMethods);
  const labelSettings = useSelector(getLabelSettings);
  const { data: insuranceSettings } = useInsurance();

  const trackedOrderDetailsByShipment = useMemo(() => {
    const { paymentType, stripeToken } = paymentMethods || {};
    return getTrackedOrderDetailsByShipmentFromOrders({
      batchEntries,
      defaultParcelTemplate,
      defaultReturnAddress,
      defaultSenderAddress,
      insuranceSettings: insuranceSettings || undefined,
      labelSettings,
      ordersData,
      paymentType,
      ratesForOrders,
      selectedOrders,
      stripeToken,
    });
  }, [
    ordersData,
    selectedOrders,
    batchEntries,
    ratesForOrders,
    defaultSenderAddress,
    defaultReturnAddress,
    defaultParcelTemplate,
    paymentMethods,
    insuranceSettings,
    labelSettings,
  ]);

  const userDetails = useMemo(() => {
    const { paymentType } = paymentMethods || {};
    return {
      defaultInsuranceSetings: insuranceSettings || undefined,
      defaultParcelTemplate: defaultParcelTemplate?.objectId,
      defaultReturnAddress: defaultReturnAddress?.id,
      defaultSenderAddress: defaultSenderAddress?.id,
      paymentType,
    };
  }, [
    defaultSenderAddress,
    defaultReturnAddress,
    defaultParcelTemplate,
    paymentMethods,
    insuranceSettings,
  ]);

  return { trackedOrderDetailsByShipment, userDetails };
};

export const getTrackedOrderDetailsByShipmentFromState = (state: ApplicationState) => {
  const selectedOrders = getSelectedOrders(state);
  const batchEntryIds = getBatchEntryIds(selectedOrders);
  const ordersData = getOrdersData(state);
  const batchEntries = getBatchEntryData(state);
  const ratesForOrders = getRatesForBatchEntriesToBuy(state, { batchEntryIds });
  const defaultSenderAddress = getUserAddress(state);
  const defaultReturnAddress = getUserReturnAddress(state);
  const defaultParcelTemplate = getDefaultParcel(state);
  const paymentMethods = getPaymentMethods(state);
  const labelSettings = getLabelSettings(state);
  const insuranceSettings = trackingHelper.userDetails?.defaultInsuranceSetings;
  const { paymentType, stripeToken } = paymentMethods || {};

  const trackedOrderDetailsByShipment = getTrackedOrderDetailsByShipmentFromOrders({
    batchEntries,
    defaultParcelTemplate,
    defaultReturnAddress,
    defaultSenderAddress,
    insuranceSettings: insuranceSettings || undefined,
    labelSettings,
    ordersData,
    paymentType,
    ratesForOrders,
    selectedOrders,
    stripeToken,
  });

  return { trackedOrderDetailsByShipment };
};

export const updateTrackedOrderDetailsByShipmentSelectedRate = (
  trackedOrderDetailsByShipment: TrackedOrderDetailsByShipment,
  trackingReference: TrackingReference,
  rates: Rate[],
  batchEntryData: HashMap<number, BatchEntryExtended>,
): TrackedOrderDetailsByShipment => {
  if (trackingReference.orderIds) {
    return trackingReference.orderIds.reduce(
      (old: TrackedOrderDetailsByShipment, orderId: string) => {
        if (!trackingReference.selectedOrders) return old;

        return updateSelectedRateForOrder({
          batchEntryData,
          orderId,
          rates,
          selectedOrders: trackingReference.selectedOrders,
          trackedOrderDetailsByShipment: old,
        });
      },
      trackedOrderDetailsByShipment,
    );
  }

  if (trackingReference.orderId && trackingReference.selectedOrders) {
    return updateSelectedRateForOrder({
      batchEntryData,
      orderId: trackingReference.orderId,
      rates,
      selectedOrders: trackingReference.selectedOrders,
      trackedOrderDetailsByShipment,
    });
  }

  return trackedOrderDetailsByShipment;
};

export const getTrackedOrderDetailsByShipmentFromOrders = ({
  batchEntries,
  defaultParcelTemplate,
  defaultReturnAddress,
  defaultSenderAddress,
  insuranceSettings,
  labelSettings,
  ordersData,
  paymentType,
  ratesForOrders,
  selectedOrders,
  stripeToken,
}: {
  ordersData: Record<string, Order>;
  selectedOrders: OrderSelection;
} & TrackedOrderDetailsByShipmentInput): TrackedOrderDetailsByShipment => {
  const orderIds = Object.keys(selectedOrders);
  const hasReturnOption = calculateHasReturnOption(
    selectedOrders,
    ordersData,
    batchEntries,
    defaultSenderAddress,
  );
  return orderIds.reduce((acc: TrackedOrderDetailsByShipment, orderId: string) => {
    const batchEntryIds = selectedOrders[orderId];
    const trackedOrderDetailsByShipment = getTrackedOrderDetailsByShipment({
      batchEntries,
      batchEntryIds,
      defaultParcelTemplate,
      defaultReturnAddress,
      defaultSenderAddress,
      hasReturnOption,
      insuranceSettings: insuranceSettings || undefined,
      labelSettings,
      paymentType,
      ratesForOrders,
      stripeToken,
    });

    return {
      ...acc,
      ...trackedOrderDetailsByShipment,
    };
  }, {});
};

const getTrackedOrderDetailsByShipment = ({
  batchEntries,
  batchEntryIds,
  defaultParcelTemplate,
  defaultReturnAddress,
  defaultSenderAddress,
  hasReturnOption,
  insuranceSettings,
  labelSettings,
  paymentType,
  ratesForOrders,
  stripeToken,
}: {
  batchEntryIds: number[];
  hasReturnOption: boolean;
} & TrackedOrderDetailsByShipmentInput): TrackedOrderDetailsByShipment => {
  const hasInsuranceOption = calculateHasInsuranceOption(batchEntryIds, batchEntries);
  return batchEntryIds.reduce((acc: TrackedOrderDetailsByShipment, batchEntryId: number) => {
    const trackedOrderDetails = getTrackedOrderDetails({
      batchEntries,
      batchEntryId,
      defaultParcelTemplate,
      defaultReturnAddress,
      defaultSenderAddress,
      hasInsuranceOption,
      hasReturnOption,
      insuranceSettings: insuranceSettings || undefined,
      labelSettings,
      paymentType,
      ratesForOrders,
      stripeToken,
    });

    if (trackedOrderDetails) {
      return {
        ...acc,
        [batchEntryId]: trackedOrderDetails,
      };
    }

    return acc;
  }, {});
};

export const getTrackedOrderDetails = ({
  batchEntries,
  batchEntryId,
  defaultParcelTemplate,
  defaultReturnAddress,
  defaultSenderAddress,
  hasInsuranceOption,
  hasReturnOption,
  insuranceSettings,
  labelSettings,
  paymentType,
  ratesForOrders,
  stripeToken,
}: {
  batchEntryId: number;
  hasInsuranceOption: boolean;
  hasReturnOption: boolean;
} & TrackedOrderDetailsByShipmentInput): TrackedOrderDetails | undefined => {
  if (!batchEntries) return;

  const be = batchEntries[batchEntryId];
  if (!be) return;

  const parcelDefaultSet: boolean =
    !!be.parcel?.userParcelTemplate &&
    be.parcel.userParcelTemplate.objectId === defaultParcelTemplate?.objectId;
  const rate = ratesForOrders.find((r) => r.shipment === be.shipmentObjectId);
  const paymentMethodDefaultSet = paymentType === PaymentMethodType.CreditCard;
  const returnDefaultSet = labelSettings?.isAutoCreateReturnLabels;
  const serviceLevelDefault = labelSettings?.outboundServiceLevel;
  return {
    addressRecipient: be.addressTo?.objectId,
    addressReturn: be.addressReturn?.objectId,
    addressReturnDefaultSet: isAddressSame(be.addressReturn, defaultReturnAddress),
    addressSender: be.addressFrom?.objectId,
    addressSenderDefaultSet: isAddressSame(be.addressFrom, defaultSenderAddress),
    carrier: rate?.provider,
    hasInsuranceOption,
    hasReturnOption,
    hasSignatureOption: true, // This is never optional
    insuranceDefaultSet: insuranceSettings?.is_auto_apply,
    parcel: serializeParcel(be.parcel),
    parcelCarrierTemplate: be.parcel?.carrierParcelTemplate
      ? be.parcel?.carrierParcelTemplate.token
      : undefined,
    parcelDefaultSet,
    parcelUserTemplate: be.parcel?.userParcelTemplate
      ? be.parcel?.userParcelTemplate.objectId
      : undefined,
    paymentMethod: stripeToken,
    paymentMethodDefaultSet,
    returnDefaultSet,
    serviceLevel: rate?.servicelevelName,
    serviceLevelDefault,
    signature: be.extras?.signatureConfirmation?.hasSignatureConfirmation,
  };
};

export const updateSelectedRateForOrder = ({
  batchEntryData,
  orderId,
  rates,
  selectedOrders,
  trackedOrderDetailsByShipment,
}: {
  batchEntryData: HashMap<number, BatchEntryExtended>;
  orderId: string;
  rates: Rate[];
  selectedOrders: OrderSelection;
  trackedOrderDetailsByShipment: TrackedOrderDetailsByShipment;
}): TrackedOrderDetailsByShipment => {
  const beIds = selectedOrders[orderId];
  return beIds.reduce((acc: TrackedOrderDetailsByShipment, beId: number) => {
    const existing = acc[beId];
    const be = batchEntryData[beId];
    if (!existing || !be) return acc;

    const rate = rates.find((rate) => rate.shipment === be.shipmentObjectId);
    const trackedOrderDetails = {
      ...existing,
      carrier: rate?.provider,
      serviceLevel: rate?.servicelevelName,
    };

    return {
      ...acc,
      [beId]: trackedOrderDetails,
    };
  }, trackedOrderDetailsByShipment);
};

export const getBatchEntryIds = (ordersSelected: OrderSelection) =>
  Object.keys(ordersSelected).reduce(
    (acc: number[], orderId: string) => [...acc, ...ordersSelected[orderId]],
    <number[]>[],
  );

export const isAddressSame = (address1: Maybe<Address>, address2: Maybe<APIAddress>) =>
  address1?.zip === address2?.zip &&
  address1?.state === address2?.state &&
  address1?.city === address2?.city &&
  address1?.street1 === address2?.street1 &&
  address1?.street2 === address2?.street2 &&
  address1?.streetNumber === address2?.streetNumber &&
  address1?.company === address2?.company &&
  address1?.phone === address2?.phone &&
  address1?.name === address2?.name;

const serializeParcel = (parcel: Parcel | null | undefined): string | undefined => {
  if (!parcel) return;

  if (parcel.userParcelTemplate) {
    const template = parcel.userParcelTemplate;
    const weight = template.weight || parcel.weight;
    const weightUnit = template.weightUnit || parcel.weightUnit;
    return `${template.length}x${template.width}x${template.height} ${template.distanceUnit}, ${weight} ${weightUnit}`;
  }

  if (parcel.carrierParcelTemplate) {
    const template = parcel.carrierParcelTemplate;
    const weight = parcel.weight;
    const weightUnit = parcel.weightUnit;
    return `${template.length}x${template.width}x${template.height} ${template.distanceUnit}, ${weight} ${weightUnit}`;
  }

  return `${parcel.length}x${parcel.width}x${parcel.height} ${parcel.distanceUnit}, ${parcel.weight} ${parcel.weightUnit}`;
};

export const calculateHasReturnOption = (
  selectedOrders: OrderSelection,
  ordersData?: Record<string, Order>,
  batchEntries?: HashMap<number, BatchEntryExtended>,
  defaultSenderAddress?: APIAddress | null,
): boolean => {
  if (!ordersData || !batchEntries) return false;

  const hasMultipleCountries =
    Array.from(
      new Set(
        Object.keys(selectedOrders)
          .map((orderId) => ordersData[orderId]?.toAddress?.country?.iso2)
          .filter((countryCode): countryCode is CountryIso2 => !!countryCode),
      ),
    ).length > 1;

  const selBatchEntries = getBatchEntryIds(selectedOrders)
    .map((beId) => batchEntries[beId])
    .filter((be) => !!be);

  const orderRequiresCustoms = singleOrMultiSelectionShouldShowCustoms(
    selBatchEntries,
    ordersData,
    selectedOrders,
    defaultSenderAddress || undefined,
  );

  return !hasMultipleCountries && !orderRequiresCustoms;
};

export const calculateHasInsuranceOption = (
  batchEntryIds: number[],
  batchEntries?: HashMap<number, BatchEntryExtended>,
): boolean => {
  if (!batchEntries) return false;

  const countries = batchEntryIds
    .map((beId) => batchEntries[beId]?.addressFrom?.country?.iso2)
    .filter((country): country is CountryIso2 => !!country);

  return isInsuranceAvailableForCountries(countries);
};

type TrackedOrderDetailsByShipment = Record<number, TrackedOrderDetails>;
interface TrackedOrderDetailsByShipmentInput {
  batchEntries?: HashMap<number, BatchEntryExtended>;
  defaultParcelTemplate: Maybe<ParcelTemplate>;
  defaultReturnAddress: Maybe<APIAddress>;
  defaultSenderAddress: Maybe<APIAddress>;
  insuranceSettings?: InsuranceSettings;
  labelSettings?: ReturnType<typeof getLabelSettings>;
  paymentType?: string;
  ratesForOrders: Rate[];
  stripeToken?: string;
}
